import React, { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Table from 'antd/lib/table'

import apiManager from 'helpers/apiManager'
import useQuery from 'hooks/useQuery'
import { dateFormatter } from 'helpers'
import { ROUTES } from 'helpers/constants'
import { ChevronLeftIcon, EyeIcon } from 'components'
import ProfileDataModal from './ProfileDataModal'

const AdminAISearchProfiles = () => {
  const [selectedProfile, setSelectedProfile] = useState(null)
  const [showDataModal, setShowDataModal] = useState(false)

  const { data, isLoading } = useQuery('admin/search/profiles', () =>
    apiManager.adminGetSearchProfiles()
  )

  const profiles = useMemo(
    () =>
      data?.map(profile => ({
        ...profile,
        key: profile.id,
      })) || [],
    [data]
  )

  const handleViewData = useCallback(profile => {
    setSelectedProfile(profile)
    setShowDataModal(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setShowDataModal(false)
    setSelectedProfile(null)
  }, [])

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: 'Name',
      dataIndex: 'fullName',
      key: 'fullName',
    },
    {
      title: 'Job Title',
      dataIndex: 'jobTitle',
      key: 'jobTitle',
    },
    {
      title: 'Company',
      dataIndex: 'company',
      key: 'company',
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
    },
    {
      title: 'Email',
      dataIndex: 'workEmail',
      key: 'workEmail',
      render: (text, record) => text || record.email || '-',
    },
    {
      title: 'LinkedIn',
      dataIndex: 'linkedinId',
      key: 'linkedinId',
      render: linkedinId =>
        linkedinId ? (
          <a
            href={`https://www.linkedin.com/in/${linkedinId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:text-blue-700 underline"
          >
            View Profile
          </a>
        ) : (
          '-'
        ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: text => (text ? dateFormatter.returnYYYYMMDD(text) : '-'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) =>
        record.data || record.profile || record.summary ? (
          <button
            onClick={() => handleViewData(record)}
            className="text-blue-500 hover:text-blue-700"
          >
            <EyeIcon className="w-5 h-5" />
          </button>
        ) : (
          <span className="text-gray-400">-</span>
        ),
    },
  ]

  return (
    <div className="w-full min-h-screen px-4 bg-white py-4 md:px-10">
      <Link
        to={ROUTES.ADMIN_AI_SEARCH}
        className="inline-flex items-center text-blue-500 hover:text-blue-700 mb-4"
      >
        <ChevronLeftIcon className="w-5 h-5 mr-1" />
        Back to AI Search
      </Link>
      <h1 className="text-4xl text-center mb-8">Search Profiles</h1>
      <div>
        <Table
          columns={columns}
          dataSource={profiles}
          loading={isLoading}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
          }}
        />
      </div>
      <ProfileDataModal show={showDataModal} profile={selectedProfile} onClose={handleCloseModal} />
    </div>
  )
}

export default AdminAISearchProfiles
